import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc as firestoreDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { getPatientReportContentPathSegments } from '../constants/adultAutismFirestorePaths';
import { STAGE_TO_REPORT_PREFIX } from '../constants/adultAutismStages';
import { appendReportAuditEntry } from './reportAuditHelpers';

/**
 * Regenerate ALL AI paragraphs for one Adult Autism assessment stage.
 *
 * This is the modular (subcollection) analogue of the ADHD
 * TriggerRegenerateCIStage / TriggerRegenerateSSIStage utilities. It exists for
 * the "forms changed after generation" case: a patient (or informant) updates
 * their pre-assessment questionnaires AFTER the clinician has already generated
 * a stage's paragraphs, and the clinician makes no further proforma edits.
 * Without an explicit trigger nothing would re-run, leaving the report built on
 * stale inputs. The clinician fires this from the Autism Documents Dashboard.
 *
 * Behaviour (mirrors the ADHD utilities, adapted for the modular data model):
 *  - Clears every paragraph approval AND the stage-level approval flag on the
 *    reportContent/{stageKey} subcollection doc, so all paragraphs return to
 *    "needs review" once the clinician reopens the review page. (The review
 *    pages derive per-paragraph status from `approvedParagraphs`, so leaving it
 *    populated would incorrectly keep unchanged paragraphs green.)
 *  - Calls GenerateDraftWithLLM once per section with
 *    force_regenerate_keys: ['__ALL__'] so the backend regenerates every
 *    paragraph regardless of previously-stored inputs/edits, and passes the
 *    patient `conditions` (e.g. ['patientIsFemale']) so conditionally-gated
 *    paragraphs (Women's Health) are included for the right patients.
 *  - Awaits all generations (no fire-and-forget) so the caller can show a
 *    spinner and only close the confirm dialog once the work is done.
 *
 * IMPORTANT — what this deliberately does NOT touch:
 *  - The render/PDF status field `${prefix}ReportProcessingStatus`. That field
 *    drives the dashboard's amber "Finalising…" spinner for the *document
 *    render* pipeline; LLM regeneration is a separate concern whose progress is
 *    surfaced by the confirm dialog's own button state. Writing it here would
 *    make the row look like a PDF render is in flight when it isn't.
 *  - The `documents.*` rendered-PDF metadata. The stale PDF stays previewable
 *    until the clinician re-approves and a fresh render is triggered, exactly
 *    as in the ADHD flow.
 *
 * @param {Object}   params
 * @param {string}   params.patientId      Firestore patient document ID.
 * @param {string}   params.clinicianId    UID of the clinician initiating it.
 * @param {string}   params.stageKey       Autism stage key (e.g. 'history').
 * @param {string[]} params.sectionTitles  Report section titles to regenerate
 *                                          (must match the backend
 *                                          SECTION_PARAGRAPHS keys).
 * @param {string[]} [params.conditions]   Conditional flags (e.g.
 *                                          ['patientIsFemale']).
 * @returns {Promise<{success: boolean, error?: any}>}
 */

// Maps Autism stage keys to the report stage label the GenerateDraftWithLLM
// Cloud Function uses to route to the correct backend processor. Mirrors the
// map in useModularSectionExitRegeneration so both trigger paths stay aligned.
const STAGE_KEY_TO_REPORT_STAGE = {
  history: 'Autism History',
  semiStructuredInterview: 'Autism Semi-Structured Interview',
};

export const TriggerRegenerateAutismStage = async ({
  patientId,
  clinicianId,
  stageKey,
  sectionTitles,
  conditions = [],
}) => {
  const user = auth.currentUser;
  if (!user || !user.uid) return { success: false, error: 'User not authenticated' };

  const reportStage = STAGE_KEY_TO_REPORT_STAGE[stageKey];
  const prefix = STAGE_TO_REPORT_PREFIX[stageKey];

  if (
    !patientId ||
    !clinicianId ||
    !reportStage ||
    !prefix ||
    !Array.isArray(sectionTitles) ||
    sectionTitles.length === 0
  ) {
    return { success: false, error: 'Missing or invalid parameters' };
  }

  const reportContentRef = firestoreDoc(
    db,
    ...getPatientReportContentPathSegments(patientId, stageKey),
  );
  const GenerateDraftWithLLM = httpsCallable(functions, 'GenerateDraftWithLLM');

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Small retry wrapper mirroring TriggerRegenerateSSIStage: a short initial
  // delay lets the approval-reset write settle, and transient failures are
  // retried with linear backoff. deadline-exceeded is rethrown immediately
  // because the generation likely still completed server-side.
  const callWithRetry = async (params, maxRetries = 2) => {
    let attempt = 0;
    await delay(400);
    while (attempt <= maxRetries) {
      try {
        return await GenerateDraftWithLLM(params);
      } catch (err) {
        if (err?.code === 'deadline-exceeded') throw err;
        if (attempt === maxRetries) throw err;
        await delay(1000 * (attempt + 1));
        attempt += 1;
      }
    }
  };

  try {
    // Wipe all prior approvals + the stage approval flag so every paragraph
    // returns to "needs review". A transaction keeps the rest of the doc
    // (outputs, inputs, editHistory) intact and avoids clobbering concurrent
    // writes. We intentionally leave ${prefix}ReportProcessingStatus and the
    // documents.* metadata untouched (see the doc comment above).
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(reportContentRef);
      if (!snap.exists()) {
        throw new Error(`reportContent/${stageKey} document does not exist`);
      }
      transaction.update(reportContentRef, {
        approvedParagraphs: {},
        [`${prefix}ReportApproved`]: false,
        [`${prefix}ReportApprovedBy`]: null,
        [`${prefix}ReportApprovedByName`]: null,
        [`${prefix}ReportApprovedAt`]: null,
        lastReviewedAt: serverTimestamp(),
      });
    });

    appendReportAuditEntry(patientId, {
      module: prefix,
      eventType: 'approvalReset',
      isFirstOccurrence: false,
    });

    // Regenerate each section sequentially. Sequential (not parallel) calls
    // keep peak LLM concurrency low and match the ADHD utilities' behaviour.
    for (const sectionTitle of sectionTitles) {
      await callWithRetry({
        clinicianId,
        patientId,
        reportStage,
        reportSection: sectionTitle,
        isTest: false,
        conditions,
        force_regenerate_keys: ['__ALL__'],
      });
    }

    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};
